import { useEffect, useState } from 'react'
import type { AddressSituation } from '@/lib/energyHistory'
import type { BatteryStrategy } from '@/lib/batterySimulation'
import type { EnergyContract } from '@/lib/batterySizing'
import { trackEvent } from '@/lib/analytics'
import { Spinner } from '@/components/Spinner'

export interface QuickWizardResult {
  contract: EnergyContract
  hasSolar: boolean
  strategy: BatteryStrategy
  priceMode: 'spot'
}

function RadioCard({
  checked,
  onSelect,
  title,
  text,
}: {
  checked: boolean
  onSelect: () => void
  title: string
  text?: string
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={checked}
      className={`flex w-full items-start gap-4 rounded-2xl border-2 p-4 text-left transition-all duration-300 md:p-5 ${
        checked ? 'border-dark-purple bg-white' : 'border-transparent bg-beige hover:bg-beige-2/40'
      }`}
    >
      <span
        className={`mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-full border-2 ${
          checked ? 'border-dark-purple' : 'border-dark-blue/30'
        }`}
        aria-hidden="true"
      >
        {checked && <span className="size-3 rounded-full bg-dark-purple" />}
      </span>
      <span>
        <span className="block text-lg font-semibold text-dark-blue">{title}</span>
        {text && (
          <span className="mt-0.5 block text-base leading-normal text-dark-blue/60">{text}</span>
        )}
      </span>
    </button>
  )
}

/**
 * One-question flow for easyEnergy: everyone has a dynamic contract, so we
 * only ask about solar. Power users can still open the expert settings.
 */
export function QuickWizard({
  situation,
  checkingSolar,
  onComplete,
  onOpenExpert,
}: {
  situation: AddressSituation | null
  checkingSolar: boolean
  onComplete: (result: QuickWizardResult) => void
  onOpenExpert: () => void
}) {
  const meterShowsReturn = (situation?.returnedKwh ?? 0) > 0
  const [hasSolar, setHasSolar] = useState<boolean | null>(null)

  useEffect(() => {
    if (!meterShowsReturn) return
    setHasSolar((current) => (current === null ? true : current))
  }, [meterShowsReturn])

  const finish = (solar: boolean) => {
    if (!solar) {
      trackEvent('battery_wizard_complete', {
        contract: 'dynamic',
        has_solar: false,
        strategy: 'trading',
        price_mode: 'spot',
        meter_suggested_solar: meterShowsReturn,
      })
      onComplete({ contract: 'dynamic', hasSolar: false, strategy: 'trading', priceMode: 'spot' })
      return
    }
    trackEvent('battery_wizard_complete', {
      contract: 'dynamic',
      has_solar: true,
      strategy: 'self-consumption-trading',
      price_mode: 'spot',
      meter_suggested_solar: meterShowsReturn,
    })
    onComplete({
      contract: 'dynamic',
      hasSolar: true,
      strategy: 'self-consumption-trading',
      priceMode: 'spot',
    })
  }

  return (
    <div className="card p-5 md:p-[30px]">
      {checkingSolar ? (
        <>
          <h3 className="text-xl font-semibold lg:text-26">We kijken of je zonnepanelen hebt</h3>
          <p className="mt-1.5 max-w-2xl text-base leading-normal text-dark-blue/60">
            Even geduld — we bekijken je slimme-meterdata. Daarna kun je het advies berekenen.
          </p>
          <div className="mt-6 flex items-center gap-3 text-base text-dark-blue/70">
            <Spinner />
            Bezig met controleren…
          </div>
        </>
      ) : (
        <>
          <h3 className="text-xl font-semibold lg:text-26">Heb je zonnepanelen?</h3>
          <p className="mt-1.5 max-w-2xl text-base leading-normal text-dark-blue/60">
            Als easyEnergy-klant rekenen we met dynamische uurprijzen. Zonnepanelen bepalen hoe de
            batterij het beste werkt: eigen opwek bewaren, of vooral handelen op de beursprijs.
          </p>
          {meterShowsReturn && (
            <p className="mt-2 max-w-2xl text-base leading-normal text-dark-blue/60">
              Je slimme meter laat zien dat je stroom teruglevert aan het net — dat wijst op
              zonnepanelen. We hebben “ja” alvast geselecteerd; je kunt dit nog aanpassen.
            </p>
          )}
          <div className="mt-5 space-y-3">
            <RadioCard
              checked={hasSolar === true}
              onSelect={() => setHasSolar(true)}
              title="Ja, ik heb zonnepanelen"
              text="De batterij bewaart je zonnestroom voor de avond, en kan een deel van de capaciteit gebruiken om te handelen op uurprijzen."
            />
            <RadioCard
              checked={hasSolar === false}
              onSelect={() => setHasSolar(false)}
              title="Nee, ik heb geen zonnepanelen"
              text="De batterij kan dan geld opleveren door te laden als stroom goedkoop is en te ontladen als stroom duur is."
            />
          </div>

          <div className="mt-6 flex flex-wrap items-center gap-3">
            <button
              className="btn-primary !px-8"
              disabled={hasSolar === null}
              onClick={() => hasSolar !== null && finish(hasSolar)}
            >
              Bereken mijn advies
            </button>
          </div>
        </>
      )}

      <p className="mt-6 border-t border-beige-2 pt-4 text-15 text-dark-blue/60">
        Liever alles zelf instellen — periode, strategie en batterijkosten?{' '}
        <button
          type="button"
          className="font-semibold text-dark-purple underline transition-colors hover:text-dark-blue"
          onClick={onOpenExpert}
        >
          Open de expertinstellingen
        </button>
      </p>
    </div>
  )
}
